import {useState, useContext, useEffect} from 'react';
import { Modal } from '../../Smart/Modal/Modal';
import { Button } from '../../Form/Button/Button';
import { buttonColors } from '../../Pickers/InputTheme';
import { I18nReactContext } from '../../../contexts/I18nContext';
import './AddSectionModal.scss';
import { v4 as uuidv4 } from 'uuid';
import { getWeezBeeCourseTemplateAPI } from '../../../Services/api';

// Template options with basic info
const TEMPLATE_OPTIONS = [
    { id: 1000, name: 'Split Layout', description: 'Split layout with bullets and video' },
    { id: 2000, name: 'Vertical Layout', description: 'Video on top with bullets below' },
    { id: 3000, name: 'Side Content', description: 'Side layout with video' },
    { id: 4000, name: 'Text Content', description: 'Text content with heading' },
    { id: 5000, name: 'Video with Text', description: 'Video with text content below' },
    { id: 'advanced', name: 'Advanced Editor', description: 'Create custom content with drag and drop components' }
];

// Default template data for each template
const getDefaultTemplateData = (templateId, i18n, language) => {
    const commonData = {
        backgroundImageUrl: '',
        logoUrl: '',
        elementsColor: '#4285f4',
        contrastColor: '#ffffff',
        textColor: '#333333',
        headTitle: i18n.translate('weezbeeCourse.editor.headTitle.defaultValue', { defaultValue: 'New Chapter' }),
        videoUrl: ''
    };

    // Template 5000 uses textContent instead of bullets
    if (templateId === 5000) {
        return {
            ...commonData,
            textContent: i18n.translate('weezbeeCourse.editor.textContent.defaultValue', { defaultValue: 'Enter your content here' })
        };
    }

    // All other templates use bullets
    return {
        ...commonData,
        bullets: [
            i18n.translate('weezbeeCourse.editor.bullets.defaultValue1', { defaultValue: 'Point 1' }),
            i18n.translate('weezbeeCourse.editor.bullets.defaultValue2', { defaultValue: 'Point 2' }),
            i18n.translate('weezbeeCourse.editor.bullets.defaultValue3', { defaultValue: 'Point 3' })
        ]
    };
};

export const AddSectionModal = ({ open, onClose, onSave }) => {
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateJson, setTemplateJson] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [templateDetails, setTemplateDetails] = useState({});
    const [allTemplatesLoading, setAllTemplatesLoading] = useState(false);
    const { i18n, language } = useContext(I18nReactContext);

    // Fetch all template JSONs when the modal opens
    useEffect(() => {
        if (open && Object.keys(templateDetails).length === 0) {
            setAllTemplatesLoading(true);

            // Create an array of promises to fetch all templates (excluding advanced editor)
            const fetchPromises = TEMPLATE_OPTIONS
                .filter(template => template.id !== 'advanced') // Skip advanced editor template
                .map(template => 
                    getWeezBeeCourseTemplateAPI({ templateId: template.id })
                        .then(response => ({ id: template.id, json: response }))
                        .catch(error => {
                            console.error(`Error fetching template JSON for template ${template.id}:`, error);
                            return { id: template.id, json: null, error };
                        })
                );

            // Wait for all promises to resolve
            Promise.all(fetchPromises)
                .then(results => {
                    const details = {};

                    // Process each template JSON to extract features
                    results.forEach(result => {
                        if (result.json) {
                            const features = [];
                            const templateData = result.json.templateData || {};

                            // Check for video support
                            if ('videoUrl' in templateData) {
                                features.push('Video');
                            }

                            // Check for bullets
                            if ('bullets' in templateData) {
                                features.push('Bullets');
                            }

                            // Check for text content
                            if ('textContent' in templateData) {
                                features.push('Text Content');
                            }

                            // Check for heading
                            if ('headTitle' in templateData) {
                                features.push('Heading');
                            }

                            // Generate a description based on features
                            let description = '';
                            if (features.includes('Video') && features.includes('Bullets') && features.includes('Heading')) {
                                if (result.id === 1000) {
                                    description = 'Split layout with bullets on the left and video on the right. Includes a heading title.';
                                } else if (result.id === 2000) {
                                    description = 'Video on top with bullets below. Includes a heading title.';
                                }
                            } else if (features.includes('Video') && !features.includes('Bullets') && !features.includes('Text Content')) {
                                description = 'Side layout with video. Simple and focused.';
                            } else if (features.includes('Text Content') && features.includes('Heading')) {
                                if (!features.includes('Video')) {
                                    description = 'Text content with heading. Good for detailed explanations.';
                                } else {
                                    description = 'Video with text content below. Good for explanations with visual aids.';
                                }
                            }

                            details[result.id] = {
                                json: result.json,
                                features,
                                description: description || TEMPLATE_OPTIONS.find(t => t.id === result.id)?.description
                            };
                        }
                    });

                    setTemplateDetails(details);
                })
                .finally(() => {
                    setAllTemplatesLoading(false);
                });
        }
    }, [open]);

    // Fetch template JSON when a template is selected
    useEffect(() => {
        if (selectedTemplate) {
            // Skip API call for advanced editor template
            if (selectedTemplate === 'advanced') {
                setTemplateJson(null);
                setIsLoading(false);
                return;
            }

            // If we already have the template details, use that
            if (templateDetails[selectedTemplate]?.json) {
                setTemplateJson(templateDetails[selectedTemplate].json);
                return;
            }

            // Otherwise fetch it
            setIsLoading(true);
            getWeezBeeCourseTemplateAPI({ templateId: selectedTemplate })
                .then(response => {
                    setTemplateJson(response);
                })
                .catch(error => {
                    console.error(`Error fetching template JSON for template ${selectedTemplate}:`, error);
                    setTemplateJson(null);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            setTemplateJson(null);
        }
    }, [selectedTemplate, templateDetails]);

    const handleTemplateSelect = (templateId) => {
        setSelectedTemplate(templateId);
    };

    const handleSave = () => {
        if (!selectedTemplate) return;

        // Create the base section object
        const newSection = {
            id: uuidv4(),
            title: i18n.translate('weezbeeCourse.editor.title.defaultValue', { defaultValue: 'New Chapter' }),
            description: i18n.translate('weezbeeCourse.editor.description.defaultValue', { defaultValue: 'Chapter description' }),
            template: selectedTemplate,
        };

        // Special handling for advanced editor template
        if (selectedTemplate === 'advanced') {
            // Initialize with empty components array for the advanced editor
            newSection.components = [];
            onSave(newSection);
            setSelectedTemplate(null);
            setTemplateJson(null);
            return;
        }

        // If we have the template JSON, use it to set up the initial data
        if (templateJson && templateJson.templateData) {
            // Initialize templateData object
            const templateData = {};

            // Process each field in the template JSON
            Object.entries(templateJson.templateData).forEach(([fieldName, fieldConfig]) => {
                // Set default value from template JSON if available, otherwise use empty value based on type
                if (fieldConfig.defaultValue !== undefined) {
                    templateData[fieldName] = fieldConfig.defaultValue;
                } else {
                    // Set appropriate empty value based on field type
                    if (fieldConfig.componentType === 'bullets' && fieldConfig.inputType === 'array') {
                        templateData[fieldName] = [
                            i18n.translate('weezbeeCourse.editor.bullets.defaultValue1', { defaultValue: 'Point 1' }),
                            i18n.translate('weezbeeCourse.editor.bullets.defaultValue2', { defaultValue: 'Point 2' }),
                            i18n.translate('weezbeeCourse.editor.bullets.defaultValue3', { defaultValue: 'Point 3' })
                        ];
                    } else if (fieldConfig.componentType === 'textarea' && fieldConfig.inputType === 'text') {
                        templateData[fieldName] = i18n.translate('weezbeeCourse.editor.textContent.defaultValue', { defaultValue: 'Enter your content here' });
                    } else if (fieldConfig.componentType === 'color' && fieldConfig.inputType === 'color') {
                        // Default colors
                        if (fieldName === 'elementsColor') templateData[fieldName] = '#4285f4';
                        else if (fieldName === 'contrastColor') templateData[fieldName] = '#ffffff';
                        else if (fieldName === 'textColor') templateData[fieldName] = '#333333';
                        else templateData[fieldName] = '#000000';
                    } else if (fieldConfig.componentType === 'text' && fieldConfig.inputType === 'text') {
                        if (fieldName === 'headTitle') {
                            templateData[fieldName] = i18n.translate('weezbeeCourse.editor.headTitle.defaultValue', { defaultValue: 'New Chapter' });
                        } else {
                            templateData[fieldName] = '';
                        }
                    } else {
                        templateData[fieldName] = '';
                    }
                }
            });

            newSection.templateData = templateData;
        } else {
            // Fallback to the old method if template JSON is not available
            newSection.templateData = getDefaultTemplateData(selectedTemplate, i18n, language);
        }

        onSave(newSection);
        setSelectedTemplate(null);
        setTemplateJson(null);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
        >
            <div className="add-section-modal">
                <h2>{i18n.translate('weezbeeCourse.editor.addSection')}</h2>
                <p className="template-selection-instruction">{i18n.translate('weezbeeCourse.editor.templateSelectionInstruction', { defaultValue: 'Select a template for your new chapter:' })}</p>

                <div className="template-grid">
                    {allTemplatesLoading ? (
                        <div className="templates-loading">
                            <span>{i18n.translate('general.loading')}</span>
                        </div>
                    ) : (
                        TEMPLATE_OPTIONS.map(template => {
                            const details = templateDetails[template.id];
                            const features = details?.features || [];
                            const description = details?.description || template.description;

                            return (
                                <div 
                                    key={template.id}
                                    className={`template-item ${selectedTemplate === template.id ? 'selected' : ''} ${isLoading && selectedTemplate === template.id ? 'loading' : ''}`}
                                    onClick={() => handleTemplateSelect(template.id)}
                                >
                                    <div className="template-preview">
                                        <div className={`template-preview-image template-${template.id}`}></div>
                                        {isLoading && selectedTemplate === template.id && (
                                            <div className="template-loading-overlay">
                                                <span>{i18n.translate('general.loading')}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="template-info">
                                        <h3>{i18n.translate(`weezbeeCourse.editor.templateName.${template.id}`, { defaultValue: template.name })}</h3>
                                        <p>{i18n.translate(`weezbeeCourse.editor.templateDescription.${template.id}`, { defaultValue: description })}</p>

                                        {features.length > 0 && (
                                            <div className="template-features">
                                                {features.map(feature => {
                                                    const featureKey = feature.toLowerCase().replace(' ', '-');
                                                    const i18nKey = `weezbeeCourse.editor.featureBadges.${featureKey}`;
                                                    return (
                                                        <span key={feature} className={`feature-badge feature-${featureKey}`}>
                                                            {i18n.translate(i18nKey, { defaultValue: feature })}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="modal-footer">
                    <Button
                        color={buttonColors.GREY}
                        onClick={onClose}
                        text={i18n.translate('general.cancel')}
                    />
                    <Button
                        color={buttonColors.YELLOW}
                        onClick={handleSave}
                        text={isLoading ? i18n.translate('general.loading') : i18n.translate('general.save')}
                        disabled={!selectedTemplate || isLoading}
                    />
                </div>
            </div>
        </Modal>
    );
};
